import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, Observable } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import { LoginDialogComponent } from '../dialogs/login-dialog/login-dialog.component';
import { BlockedMedicalComponent } from '../register-steps/non-medical/non_medical.component';
import { BlockedUserComponent } from '../register-steps/blocked-user/blocked_user.component';
import { wronguuidComponent } from '../dialogs/nouuid-dialog/nouuid-dialog.component';

@Injectable({
  providedIn: 'root'
})
export class AuthCheckService {
  private authValidSubject = new BehaviorSubject<boolean>(false);
  public authValid$ = this.authValidSubject.asObservable();
  
  // Flag to prevent dialog reopening while showing wrongUuid
  private static isShowingWrongUuid = false;

  // Required storage items
  private requiredStorageItems = [ 
    'externalAuthId', 
    'externalSource', 
  ];

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private translocoService: TranslocoService
  ) {
    // Check auth on initialization
    this.checkAuthRequirements();
  }

  /**
   * Check if all required auth data is present
   */
  public checkAuthRequirements(): boolean {
    const missingItems = this.getMissingItems();
    const isValid = missingItems.length === 0;
    
    // Update observable state
    this.authValidSubject.next(isValid);
    
    return isValid;
  }
  
  /**
   * Find which required items are missing
   */
  public getMissingItems(): string[] {
    return this.requiredStorageItems.filter(item => !this.isItemPresent(item));
  }
  
  /**
   * Check if wrongUuid dialog is currently showing
   */
  public isShowingWrongUuidDialog(): boolean {
    return AuthCheckService.isShowingWrongUuid;
  }
  
  /**
   * Check if a specific item exists in localStorage
   */
  private isItemPresent(key: string): boolean {
    const item = localStorage.getItem(key);
    return item !== null && item !== undefined && item !== '';
  }
  
  /**
   * Handle unauthorized access
   */
  public handleUnauthorized(): void {
    // Close any open dialogs first
    const loginDialogRef = this.dialog.openDialogs.find(
      dialog => dialog.componentInstance instanceof LoginDialogComponent
    );
  
    if (loginDialogRef) {
      loginDialogRef.close();
    }
    if (this.dialog.openDialogs.length > 0) {
      this.dialog.closeAll();
    }
    
    setTimeout(() => {
      // Show the auth error dialog
      this.showAuthErrorDialog();
    }, 300);
  }

  public handleUnauthorizedWeb(): void {
    console.log('[AuthCheck] 🔴 handleUnauthorizedWeb called');
    
    // Prevent handling if already showing wrongUuid
    if (AuthCheckService.isShowingWrongUuid) {
      console.log('[AuthCheck] ⛔ Already showing wrongUuid, ignoring duplicate call');
      return;
    }
    
    // Check if we're in iframe mode
    const isInIframe = window.self !== window.top;
    console.log('[AuthCheck] 📍 Mode detection:', { isInIframe });
    
    // Close any open dialogs first
    this.router.navigate([]);
  
    const loginDialogRef = this.dialog.openDialogs.find(
      dialog => dialog.componentInstance instanceof LoginDialogComponent
    );
  
    // In iframe mode, don't close dialogs - just replace with nouuid dialog
    // In standalone mode, close all dialogs first
    if (!isInIframe) {
      console.log('[AuthCheck] 🖥️ Standalone mode - closing dialogs');
      if (loginDialogRef) {
        loginDialogRef.close();
      }
      if (this.dialog.openDialogs.length > 0) {
        this.dialog.closeAll();
      }
      
      setTimeout(() => {
        // Show the auth error dialog
        this.showWrongUuidDialog();
      }, 300);
    } else {
      console.log('[AuthCheck] 🖼️ Iframe mode - showing wrongUuid');
      // In iframe mode, show nouuid dialog immediately without closing login
      this.showWrongUuidDialogInIframe();
    }
  }
  
  /**
   * Show auth error dialog
   */
  private showAuthErrorDialog(): void {
    // Open the auth error dialog
    this.dialog.open(BlockedUserComponent, {
      width: '400px',
      disableClose: true,
      data: {
        missingItems: this.getMissingItems()
      }
    }).afterClosed().subscribe(result => {
      if (result === 'refresh') {
        window.location.reload();
      } else {
        // Redirect to the login page or home page
        this.router.navigate(['/']);
      }
    });

    const loginDialogRef = this.dialog.openDialogs.find(
      dialog => dialog.componentInstance instanceof LoginDialogComponent
    );
    if (loginDialogRef) {
      loginDialogRef.close();
    }

  }

  private showWrongUuidDialogInIframe(): void {
    // In iframe mode, close all dialogs silently (without notifying parent)
    // and immediately open the nouuid dialog
    const wrongUuidDialogOpen = this.dialog.openDialogs.some(
      dialog => dialog.componentInstance instanceof wronguuidComponent
    );
    
    if (wrongUuidDialogOpen) {
      return;
    }
    
    // Set flag to prevent reopening
    AuthCheckService.isShowingWrongUuid = true;
    console.log('[AuthCheck] 🚨 Setting isShowingWrongUuid = true');
    
    // Log currently open dialogs before closing
    console.log('[AuthCheck] 📋 Open dialogs before close:', 
      this.dialog.openDialogs.map(d => ({
        id: d.id,
        component: d.componentInstance.constructor.name
      }))
    );
    
    // Close all dialogs and WAIT for them to close completely
    this.dialog.closeAll();
    
    // Use afterAllClosed to ensure dialogs are fully closed before opening wrongUuid
    this.dialog.afterAllClosed.subscribe(() => {
      console.log('[AuthCheck] 📋 All dialogs closed, now opening wrongUuid');
      
      // Open nouuid dialog after all others are closed
      console.log('[AuthCheck] 🔓 Opening wrongUuid dialog in iframe mode');
      const dialogRef = this.dialog.open(wronguuidComponent, {
        disableClose: true,
        width: '608px', // Fixed width for iframe mode
        data: {
          missingItems: this.getMissingItems()
        }
      });
      
      console.log('[AuthCheck] ✅ wrongUuid dialog opened:', dialogRef.id);
      console.log('[AuthCheck] 📋 Total open dialogs now:', this.dialog.openDialogs.length);
      
      dialogRef.afterClosed().subscribe(result => {
        // Clear flag when dialog is closed
        AuthCheckService.isShowingWrongUuid = false;
        console.log('[AuthCheck] 🔓 Setting isShowingWrongUuid = false');
        
        if (result === 'refresh') {
          window.location.reload();
        } else {
          // Redirect to the login page or home page
          this.router.navigate(['/']);
        }
      });
    });
  }

  private showWrongUuidDialog(): void {
    // Open the auth error dialog
    const wrongUuidDialogOpen = this.dialog.openDialogs.some(
    dialog => dialog.componentInstance instanceof wronguuidComponent
    );
    
    if (wrongUuidDialogOpen) {
      return;
    }
    
    // Set flag to prevent reopening
    AuthCheckService.isShowingWrongUuid = true;
    
    // Close any other open dialogs first
    if (this.dialog.openDialogs.length > 0) {
      this.dialog.closeAll();
    }

    // Open the auth error dialog after a short delay
    setTimeout(() => {
      const dialogRef = this.dialog.open(wronguuidComponent, {
        disableClose: true,
        data: {
          missingItems: this.getMissingItems()
        }
      });
      
      dialogRef.afterClosed().subscribe(result => {
        // Clear flag when dialog is closed
        AuthCheckService.isShowingWrongUuid = false;
        
        if (result === 'refresh') {
          window.location.reload();
        } else {
          // Redirect to the login page or home page
          this.router.navigate(['/']);
        }
      });
    }, 100);
  }
}